import { TEfactureArtisanSave } from '@Arti-zen/package-backoffice'
import BusinessIcon from '@mui/icons-material/Business'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { ReactElement, useState } from 'react'
import { ArtisanWithEfacture } from './EinvoicingArtisansCtrl'

export const interestLevelLabels: Record<string, string> = {
  Intéressé: 'Intéressé',
  'A relancer': 'A relancer',
  'Pas intéressé': 'Pas intéressé',
  'Pas de réponse': 'Pas de réponse',
  'PA': 'PA',
}

const reasonLabels: Record<string, string> = {
  'Comptable / PA': 'Comptable / PA',
  Septembre: 'Septembre',
  '2027': '2027',
  Agenda: 'Agenda',
  "Pas d'utilisation": "Pas d'utilisation",
  Autre: 'Autre',
}

export const interestLevelColors: Record<string, string> = {
  Intéressé: '#3B82F6',
  'A relancer': '#F59E0B',
  'Pas de réponse': '#e0e00e',
  'Pas intéressé': '#EF4444',
  'PA': '#22C55E',
}

interface Props {
  artisan: ArtisanWithEfacture
  updateEfactureArtisanList: (companyId: number, changes: any) => Promise<void>
  close: () => void
}

export default function InterestLevelContent({ artisan, updateEfactureArtisanList, close }: Props): ReactElement {
  const [loading, setLoading] = useState(false)

  const [state, setState] = useState<TEfactureArtisanSave>({
    interestLevel: artisan.interestLevel,
    reason: artisan.reason,
    comment: artisan.comment ?? '',
  })

  const onSave = async () => {
    setLoading(true)

    try {
      await updateEfactureArtisanList(artisan.id, state)
      close()
    } finally {
      setLoading(false)
    }
  }

  const handleInterestChange = (e: SelectChangeEvent) => {
    setState((s) => ({
      ...s,
      interestLevel: e.target.value as TEfactureArtisanSave['interestLevel'],
    }))
  }

  const handleReasonChange = (e: SelectChangeEvent) => {
    setState((s) => ({
      ...s,
      reason: e.target.value as TEfactureArtisanSave['reason'],
    }))
  }

  return (
    <Card elevation={3} sx={{ width: 600, borderRadius: 3 }}>
      <CardContent sx={{ p: 4 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Suivi e-facture
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" mt={1} flexWrap="wrap">
              <BusinessIcon color="action" />

              <Typography color="text.secondary">{artisan.companyName || artisan.legalName}</Typography>

              {state.interestLevel && (
                <Chip
                  size="small"
                  style={{ backgroundColor: interestLevelColors[state.interestLevel] }}
                  label={interestLevelLabels[state.interestLevel]}
                />
              )}
            </Stack>
          </Box>

          <Divider />

          <FormControl fullWidth>
            <InputLabel>Niveau d'intérêt</InputLabel>

            <Select label="Niveau d'intérêt" value={state.interestLevel ?? ''} onChange={handleInterestChange}>
              <MenuItem value="">
                <em>Aucun</em>
              </MenuItem>
              {Object.keys(interestLevelLabels).map((level) => (
                <MenuItem key={level} value={level}>
                  {interestLevelLabels[level]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Raison</InputLabel>

            <Select label="Raison" value={state.reason ?? ''} onChange={handleReasonChange}>
              <MenuItem value="">
                <em>Aucune</em>
              </MenuItem>
              {Object.keys(reasonLabels).map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reasonLabels[reason]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Commentaire"
            multiline
            minRows={5}
            fullWidth
            placeholder="Ajouter une note..."
            value={state.comment}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                comment: e.target.value,
              }))
            }
          />
          <Divider />

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <button onClick={close} disabled={loading}>
              Annuler
            </button>

            <button
              onClick={onSave}
              disabled={loading}
              style={{ backgroundColor: '#22C55E', color: 'white', padding: '8px 16px', borderRadius: '4px' }}
            >
              {loading ? 'Enregistrement en cours...' : 'Enregistrer'}
            </button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}
